import type { ChangeEventHandler, FormEventHandler } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { IPushRule, IPushRules } from '$types/matrix-sdk';
import { PushRuleKind, EventType } from '$types/matrix-sdk';
import { Box, Text, Badge, Button, Input, config, IconButton, Spinner } from 'folds';
import { menuIcon, X } from '$components/icons/phosphor';
import { useAccountData } from '$hooks/useAccountData';

import { SequenceCard, SequenceCardStyle } from '$components/sequence-card';
import { SettingTile } from '$components/setting-tile';
import { SettingMenuSelector } from '$components/setting-menu-selector';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { toSettingsFocusIdPart } from '$features/settings/settingsLink';
import type { NotificationModeOptions } from '$hooks/useNotificationMode';
import {
  getNotificationModeActions,
  NotificationMode,
  useNotificationActionsMode,
  useNotificationModeActions,
} from '$hooks/useNotificationMode';
import { AsyncStatus, useAsyncCallback } from '$hooks/useAsyncCallback';
import { NotificationLevelsHint } from './NotificationLevelsHint';
import { notificationModeSelectorOptions } from './notificationModeOptions';

const NOTIFY_MODE_OPS: NotificationModeOptions = {
  highlight: true,
};

function KeywordInput() {
  const mx = useMatrixClient();
  const [keyword, setKeyword] = useState('');

  const [keywordState, addKeyword] = useAsyncCallback(
    useCallback(
      async (k: string) => {
        mx.addPushRule('global', PushRuleKind.ContentSpecific, k, {
          actions: getNotificationModeActions(NotificationMode.Notify, NOTIFY_MODE_OPS),
          pattern: k,
        });
        setKeyword('');
      },
      [mx]
    )
  );
  const addingKeyword = keywordState.status === AsyncStatus.Loading;

  const handleChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    const k = evt.currentTarget.value;
    setKeyword(k);
  };

  const handleReset = () => {
    setKeyword('');
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (evt) => {
    evt.preventDefault();
    if (addingKeyword) return;

    const target = evt.target as HTMLFormElement | undefined;
    const keywordInput = target?.keywordInput as HTMLInputElement | undefined;
    const k = keywordInput?.value.trim();
    if (!k) return;

    addKeyword(k);
  };

  return (
    <Box as="form" onSubmit={handleSubmit} gap="200" aria-disabled={addingKeyword}>
      <Box grow="Yes" direction="Column">
        <Input
          required
          name="keywordInput"
          value={keyword}
          onChange={handleChange}
          variant="Secondary"
          radii="300"
          style={{ paddingRight: config.space.S200 }}
          readOnly={addingKeyword}
          after={
            keyword &&
            !addingKeyword && (
              <IconButton
                type="reset"
                onClick={handleReset}
                size="300"
                radii="300"
                variant="Secondary"
              >
                {menuIcon(X)}
              </IconButton>
            )
          }
        />
      </Box>
      <Button
        size="400"
        variant="Secondary"
        fill="Soft"
        outlined
        radii="300"
        type="submit"
        disabled={addingKeyword}
      >
        {addingKeyword && <Spinner variant="Secondary" size="300" />}
        <Text size="B400">Save</Text>
      </Button>
    </Box>
  );
}

type PushRulesProps = {
  pushRule: IPushRule;
};

function KeywordCross({ pushRule }: PushRulesProps) {
  const mx = useMatrixClient();
  const [removeState, remove] = useAsyncCallback(
    useCallback(
      () => mx.deletePushRule('global', PushRuleKind.ContentSpecific, pushRule.rule_id),
      [mx, pushRule]
    )
  );

  const removing = removeState.status === AsyncStatus.Loading;
  return (
    <IconButton onClick={remove} size="300" radii="Pill" variant="Secondary" disabled={removing}>
      {removing ? <Spinner size="100" /> : menuIcon(X)}
    </IconButton>
  );
}

function KeywordModeSwitcher({ pushRule }: PushRulesProps) {
  const mx = useMatrixClient();

  const getModeActions = useNotificationModeActions(NOTIFY_MODE_OPS);
  const selectedMode = useNotificationActionsMode(pushRule.actions);
  const [changeState, change] = useAsyncCallback(
    useCallback(
      async (mode: NotificationMode) => {
        const actions = getModeActions(mode);
        await mx.setPushRuleActions(
          'global',
          PushRuleKind.ContentSpecific,
          pushRule.rule_id,
          actions
        );
      },
      [mx, getModeActions, pushRule]
    )
  );

  return (
    <SettingMenuSelector
      value={selectedMode}
      options={notificationModeSelectorOptions}
      onSelect={change}
      loading={changeState.status === AsyncStatus.Loading}
    />
  );
}

export function KeywordMessagesNotifications() {
  const pushRulesEvt = useAccountData(EventType.PushRules);
  const pushRules = useMemo(
    () => pushRulesEvt?.getContent<IPushRules>() ?? { global: {} },
    [pushRulesEvt]
  );

  const keywordPushRules = useMemo(() => {
    const content = pushRules.global.content ?? [];
    return content.filter((pushRule) => !pushRule.default && typeof pushRule.pattern === 'string');
  }, [pushRules]);

  return (
    <Box direction="Column" gap="100">
      <Box alignItems="Center" justifyContent="SpaceBetween" gap="200">
        <Text size="L400">Keyword Messages</Text>
        <Box gap="100" alignItems="Center">
          <NotificationLevelsHint />
          <Text size="T200">Badge: </Text>
          <Badge radii="300" variant="Success" fill="Solid">
            <Text size="L400">1</Text>
          </Badge>
        </Box>
      </Box>
      <Text size="T300" priority="300">
        Custom keywords that trigger notifications when matched in a message body.
      </Text>
      <SequenceCard
        className={SequenceCardStyle}
        variant="SurfaceVariant"
        direction="Column"
        gap="400"
      >
        <SettingTile
          title="Select Keyword"
          focusId="select-keyword"
          description="Set a notification preference for message containing given keyword."
        >
          <KeywordInput />
        </SettingTile>
      </SequenceCard>
      {keywordPushRules.map((pushRule) => (
        <SequenceCard
          key={pushRule.rule_id}
          className={SequenceCardStyle}
          variant="SurfaceVariant"
          direction="Column"
          gap="400"
        >
          <SettingTile
            title={`"${pushRule.pattern}"`}
            focusId={`keyword-${toSettingsFocusIdPart(
              pushRule.pattern ?? pushRule.rule_id ?? 'custom-keyword'
            )}`}
            showSettingLinkAction={false}
            before={<KeywordCross pushRule={pushRule} />}
            after={<KeywordModeSwitcher pushRule={pushRule} />}
          />
        </SequenceCard>
      ))}
    </Box>
  );
}
